import { Avatar, Box, Button, CircularProgress, IconButton, styled, TextField, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { AuthContext } from "../utils/AuthContext";
import { updateProfile } from "../services/ProfileService";
import { toast } from 'react-toastify';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import Global from "../utils/Global";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});


const ProfileForm = () => {

  const { auth, setAuth } = useContext(AuthContext);


  const [nickname, setNickname] = useState(auth.user.nick_name);
  const [secondaryNumber, setSecondaryNumber] = useState(auth.user.agent ? auth.user.agent.secondary_number : '');
  const [bio, setBio] = useState(auth.user.agent ? auth.user.agent.bio : '');
  const [isProfileRemoved, setProfileRemoved] = useState(false);
  const [image, setImage] = useState(null);
  const [image64, setImage64] = useState(null);


  const [nicknameError, setNicknameError] = useState('');
  const [secondaryNumberError, setSecondaryNumberError] = useState('');
  const [bioError, setBioError] = useState('');

  const [isLoading, setLoading] = useState(false);

  function validateForm() {
    let valid = true;

    setNicknameError('');
    setSecondaryNumberError('');
    setBioError('');

    const nick_name = nickname ? nickname.trim() : '';
    const secondary_number = secondaryNumber ? secondaryNumber.trim() : '';
    const bio_trimmed = bio ? bio.trim() : '';

    if (nick_name === '') {
      setNicknameError('Enter your nick name.');

      valid = false;
    } else if (nick_name.length > 35) {
      setNicknameError('Nick name is too long.');

      valid = false;
    }

    if (secondary_number.length > 35) {
      setSecondaryNumberError('Secondary number is too long.');

      valid = false;
    }

    if (bio_trimmed.length > 500) {
      setBioError('Bio is too long.');

      valid = false;
    }

    return valid;
  }

  function handleUpdateProfile(event) {
    event.preventDefault();

    const isValid = validateForm();

    if (!isValid) {
      return;
    }


    const data = new FormData();

    if (image) {
      data.append('image', image);
    }

    data.append('nick_name', nickname || '');
    data.append('secondary_number', secondaryNumber || '');
    data.append('bio', bio || '');
    data.append('is_profile_removed', isProfileRemoved);

    setLoading(true);

    updateProfile({
      token: auth.token, data
    }).then(response => {
      auth.user = response.data.user;

      localStorage.setItem('agent-connect-current-user', JSON.stringify(auth.user));

      setAuth({
        token: auth.token,
        user: auth.user
      });

      setLoading(false);

      toast.success("Profile has been updated", {
        autoClose: 2000,
        hideProgressBar: true,
        icon: ({ theme, type }) => <CheckIcon sx={{ color: '#3328BF' }} />,
        closeButton: false
      });
    }).catch(err => {
      setLoading(false);

      const result = displayErrors(err.response.data);

      if (!result) {
        toast.error(err.response.data.message, {
          autoClose: 2000,
          hideProgressBar: true,
          closeButton: false
        });
      }


    });

  }

  function displayErrors(data) {
    if (data.hasOwnProperty('errors')) {
      if (data.errors.hasOwnProperty('nick_name')) {
        setNicknameError(data.errors.nick_name);
      }

      if (data.errors.hasOwnProperty('secondary_number')) {
        setSecondaryNumberError(data.errors.secondary_number);
      }

      if (data.errors.hasOwnProperty('bio')) {
        setBioError(data.errors.bio);
      }
    } else {
      return false;
    }


    return true;
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]; // Get the selected file

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("The file size exceeds the 5MB limit. Please upload a smaller image.");
        return;
      }

      setProfileRemoved(false);

      setImage(file);


      const reader = new FileReader();
      reader.onload = (event) => {
        setImage64(event.target.result); // Set the base64 URL to the state
      };
      reader.readAsDataURL(file); // Convert image to base64
    }
  };

  function handleRemoveProfileClicked() {
    setProfileRemoved(true);
    setImage(null);
    setImage64(null);
  }

  return (
    <Box>
      <Box>
        <Typography variant="h5">
          Setting
        </Typography>
        <Typography variant="p" sx={{ color: '#9e9e9e' }}>
          Manage your account profile
        </Typography>
      </Box>

      <form onSubmit={handleUpdateProfile}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Avatar sx={{ width: 100, height: 100 }} src={isProfileRemoved ? null : (image64 ? image64 : Global.storageURL + auth.user.image)} />

          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              onClick={handleRemoveProfileClicked}
              className="remove-profile-photo"
              sx={{
                position: 'relative',
                top: -100,
                left: 40,
                backgroundColor: '#bdbdbd',
                width: 24,
                height: 24
              }}>
              <CloseIcon
                sx={{ fontSize: 16 }}
              />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: -1 }}>
          <Typography sx={{ fontWeight: 600 }}>Profile Photo</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          {/* <input
                                type="file"
                                // ref={inputFileRef}
                                onChange={(e) => {
                                    setImage(e.target.files[0]);
                                }}
                                accept="image/*"
                            /> */}

          <Button
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            sx={{ textTransform: 'none' }}
          >
            Change profile photo
            <VisuallyHiddenInput
              type="file"
              onChange={handleImageChange}
              accept=".png, .jpg, .jpeg"
            />
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ mt: 3 }}>
            <TextField
              label="Full name"
              variant="filled"
              fullWidth
              sx={{
                '& .MuiFilledInput-root': {
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&.Mui-focused': {
                    boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                  },
                },
              }}
              value={auth.user.full_name}
              type='text'
              disabled
            />
          </Box>
          {/* <Box sx={{ mt: 3 }}>
            <label for="full_name" style={{ fontWeight: 600, color: '#212121' }}>Full name</label>
            <TextField
              size="small"
              name="Full Name"
              id="full_name"
              fullWidth
              value={auth.user.full_name}
              disabled
              sx={{ mt: 1 }}
            />
          </Box> */}
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Nick name"
              placeholder="Enter your nick name"
              variant="filled"
              fullWidth
              sx={{
                '& .MuiFilledInput-root': {
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&.Mui-focused': {
                    boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                  },
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                },
              }}
              value={nickname}
              onChange={(e) => {
                setNickname(e.target.value);
              }}
              type='text'
              error={nicknameError.length > 0}
              helperText={nicknameError}
            />
          </Box>

          {/* <Box sx={{ mt: 2 }}>
            <label for="nick_name" style={{ fontWeight: 600, color: '#212121' }}>Nick name</label>
            <TextField
              size="small"
              name="Nick Name"
              id="nick_name"
              placeholder="Enter nick name"
              value={nickname}
              onChange={(e) => {
                setNickname(e.target.value);
              }}
              fullWidth
              error={nicknameError.length > 0}
              helperText={nicknameError}
              sx={{ mt: 1 }}
            />
          </Box> */}

          <Box sx={{ mt: 2 }}>
            <TextField
              label="Email"
              variant="filled"
              fullWidth
              sx={{
                '& .MuiFilledInput-root': {
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  '&.Mui-focused': {
                    boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                  },
                },
              }}
              value={auth.user.email}
              type='email'
              disabled
            />
          </Box>

          {/* <Box sx={{ mt: 2 }}>
            <label for="email" style={{ fontWeight: 600, color: '#212121' }}>Email</label>
            <TextField
              size="small"
              name="Email"
              id="email"
              fullWidth
              value={auth.user.email}
              disabled
              sx={{ mt: 1 }}
            />
          </Box> */}


          <Box sx={{ display: auth.user.user_type === 'Agent' ? 'block' : 'none' }}>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Contact number"
                variant="filled"
                fullWidth
                sx={{
                  '& .MuiFilledInput-root': {
                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                    '&.Mui-focused': {
                      boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                    },
                  },
                }}
                value={auth.user.agent ? auth.user.agent.contact_number : ''}
                type='text'
                disabled
              />
            </Box>

            {/* <Box sx={{ mt: 2 }}>
              <label for="contact_number" style={{ fontWeight: 600, color: '#212121' }}>Contact number</label>
              <TextField
                size="small"
                name="Contact Number"
                id="contact_number"
                fullWidth
                value={auth.user.agent ? auth.user.agent.contact_number : ''}
                disabled
                sx={{ mt: 1 }}
              />
            </Box> */}

            <Box sx={{ mt: 2 }}>
              <TextField
                label="Secondary number"
                placeholder="Enter secondary number"
                variant="filled"
                fullWidth
                sx={{
                  '& .MuiFilledInput-root': {
                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                    '&.Mui-focused': {
                      boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                    },
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                }}
                type='text'
                value={secondaryNumber}
                onChange={(e) => {
                  setSecondaryNumber(e.target.value);
                }}
                error={secondaryNumberError.length > 0}
                helperText={secondaryNumberError}
              />
            </Box>

            {/* <Box sx={{ mt: 2 }}>
              <label for="secondary_number" style={{ fontWeight: 600, color: '#212121' }}>Secondary number</label>
              <TextField
                size="small"
                name="Secondary number"
                placeholder="Enter secondary number"
                id="secondary_number"
                value={secondaryNumber}
                onChange={(e) => {
                  setSecondaryNumber(e.target.value);
                }}
                fullWidth
                error={secondaryNumberError.length > 0}
                helperText={secondaryNumberError}
                sx={{ mt: 1 }}
              />
            </Box> */}

            <Box sx={{ mt: 2 }}>
              <TextField
                label="Bio"
                placeholder="Enter bio"
                variant="filled"
                fullWidth
                sx={{
                  '& .MuiFilledInput-root': {
                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                    '&.Mui-focused': {
                      boxShadow: '0 0 5px rgba(51, 40, 191, 0.4)',
                    },
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                }}
                type='text'
                value={bio}
                onChange={(e) => {
                  setBio(e.target.value);
                }}
                multiline
                rows={3}
                error={bioError.length > 0}
                helperText={bioError}
              />
            </Box>
            {/* <Box sx={{ mt: 2 }}>
              <label for="bio" style={{ fontWeight: 600, color: '#212121' }}>Bio</label>
              <TextField
                size="small"
                name="Bio"
                placeholder="Enter bio"
                id="bio"
                fullWidth
                value={bio}
                onChange={(e) => {
                  setBio(e.target.value);
                }}
                multiline
                rows={3}
                error={bioError.length > 0}
                helperText={bioError}
                sx={{ mt: 1 }}
              />
            </Box> */}
          </Box>



          <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
            <Button
              disabled={isLoading}
              type="submit"
              variant="contained"
              sx={{ textTransform: 'none' }}
              size="large"
            >
              Update Profile
            </Button>
            {
              isLoading &&
              <CircularProgress size={24} sx={{ ml: 2 }} />
            }
          </Box>
        </Box>
      </form>
    </Box>
  );
}

export default ProfileForm;